import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { getGetSupervisorPhotoUrlQueryOptions } from '@/common/api/endpoints/oral-exams/oral-exams';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import type { OralExamStudentSupervisorFaceResponse } from '@/common/api/models/oralExamStudentSupervisorFaceResponse';
import { OralExamSupervisorStatus } from '@/common/api/models/oralExamSupervisorStatus';
import type { OralExamRouteIds } from '@/common/hooks/oralExam/useOralExamRunStatePoll';
import { isSelfServiceRun } from '@/common/utils/oralExam/runConduct';

import type { SupervisorFaceDisplay } from '../components/SupervisorFrame';

import { useSupervisorPhotoUrl } from './supervisorPhotoUrl';

const EMPTY_SUPERVISOR_FACES: OralExamStudentSupervisorFaceResponse[] = [];

function mapFaceWithUrl(
  face: OralExamStudentSupervisorFaceResponse,
  url: string | null | undefined,
): SupervisorFaceDisplay {
  return {
    lecturerId: face.lecturerId,
    name: face.displayName ?? 'Supervisor',
    photoUrl: url ?? null,
  };
}

function isSupervisorWellLive(status: OralExamSupervisorStatus | undefined): boolean {
  return status === OralExamSupervisorStatus.here_with_you
    || status === OralExamSupervisorStatus.engaged;
}

export type StudentSupervisorFrameProps = {
  supervisorName?: string;
  supervisorPhotoUrl: string | null;
  supervisorFaces: SupervisorFaceDisplay[];
  visitingLecturerId: string | null;
};

/**
 * Derives SupervisorFrame props from run state, resolving per-supervisor photo URLs.
 */
export function useStudentSupervisorFrame(
  ids: OralExamRouteIds | null,
  runState: OralExamRunStateResponse | null | undefined,
): StudentSupervisorFrameProps {
  const view = runState?.supervisorView;
  const faces = useMemo(
    () => view?.faces ?? EMPTY_SUPERVISOR_FACES,
    [view?.faces],
  );
  const visitor = view?.visitor ?? null;
  const supervisorStatus = runState?.supervisorStatus;
  const wellLive = isSupervisorWellLive(supervisorStatus);
  const selfService = isSelfServiceRun(runState);

  const legacyPhotoUrl = useSupervisorPhotoUrl(
    selfService ? null : ids,
    selfService ? null : runState?.supervisorPhotoBlobPath,
    { forceEnabled: !selfService },
  );

  const photoTargets = useMemo(() => {
    if (faces.length === 0) {
      return [] as OralExamStudentSupervisorFaceResponse[];
    }
    if (wellLive && visitor) {
      return [visitor];
    }
    return faces;
  }, [faces, visitor, wellLive]);

  const photoQueries = useQueries({
    queries: photoTargets.map((face) => ({
      ...getGetSupervisorPhotoUrlQueryOptions(
        ids?.courseId ?? '',
        ids?.moduleId ?? '',
        ids?.taskId ?? '',
        ids?.bookingId ?? '',
        { lecturer_id: face.lecturerId },
        {
          query: {
            enabled: Boolean(ids) && !selfService,
          },
        },
      ),
    })),
  });

  const urlByLecturerId = useMemo(() => {
    const map = new Map<string, string | null>();
    photoTargets.forEach((face, index) => {
      map.set(face.lecturerId, photoQueries[index]?.data?.data?.data?.url ?? null);
    });
    return map;
  }, [photoTargets, photoQueries]);

  return useMemo(() => {
    if (selfService) {
      return {
        supervisorName: undefined,
        supervisorPhotoUrl: null,
        supervisorFaces: [],
        visitingLecturerId: null,
      };
    }
    if (faces.length === 0) {
      return {
        supervisorName: runState?.supervisorName ?? undefined,
        supervisorPhotoUrl: legacyPhotoUrl,
        supervisorFaces: [],
        visitingLecturerId: null,
      };
    }

    const supervisorFaces = faces.map(
      (face) => mapFaceWithUrl(face, urlByLecturerId.get(face.lecturerId)),
    );

    const visitingLecturerId = wellLive && visitor ? visitor.lecturerId : null;

    let supervisorName = runState?.supervisorName ?? undefined;
    if (wellLive && visitor) {
      supervisorName = visitor.displayName ?? supervisorName;
    } else if (!supervisorName && faces.length === 1) {
      supervisorName = faces[0].displayName ?? undefined;
    }

    let supervisorPhotoUrl: string | null = legacyPhotoUrl;
    if (wellLive && visitor) {
      supervisorPhotoUrl = urlByLecturerId.get(visitor.lecturerId) ?? legacyPhotoUrl;
    } else if (faces.length === 1) {
      supervisorPhotoUrl = urlByLecturerId.get(faces[0].lecturerId) ?? legacyPhotoUrl;
    } else {
      supervisorPhotoUrl = legacyPhotoUrl;
    }

    return {
      supervisorName,
      supervisorPhotoUrl,
      supervisorFaces,
      visitingLecturerId,
    };
  }, [
    faces,
    legacyPhotoUrl,
    selfService,
    runState?.supervisorName,
    urlByLecturerId,
    visitor,
    wellLive,
  ]);
}
